/**
 * HTTP Client for account API calls
 */

import {
  getProfile,
  updateProfile,
  updatePassword,
  checkPin,
  addPin,
  updatePin,
  getBalanceHistory,
  getBalanceHistoryDetail,
  getBankAccounts,
  checkBankAccount,
  getBankList,
  addBankAccount,
  updateBankAccount,
  deleteBankAccount,
  getAddresses,
  getAddressDetail,
  addAddress,
  updateAddress,
  deleteAddress,
  setMainAddress,
  getVersion,
} from "./utils";

class HttpAccountClient {
  /**
   * Generic request handler with consistent error handling
   * @param {Promise} apiCall - API call to execute
   * @returns {Promise} Formatted response or error
   */
  async #handleRequest(apiCall) {
    try {
      const response = await apiCall
      return {
        success: true,
        data: response.data.data,
        status: response.status,
        message: response.data.message,
        meta: response.data.meta,
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || error.message,
        status: error.response?.status || 500,
      }
    }
  }

  /**
   * Get user profile
   * @returns {Promise} Response from API
   */
  getProfile() {
    return this.#handleRequest(getProfile())
  }

  /**
   * Update user profile
   * @param {Object} params - Profile update parameters
   * @returns {Promise} Response from API
   */
  updateProfile(params = {}) {
    return this.#handleRequest(updateProfile(params))
  }

  /**
   * Update user password
   * @param {Object} params - Password update parameters
   * @returns {Promise} Response from API
   */
  updatePassword(params = {}) {
    return this.#handleRequest(updatePassword(params))
  }

  /**
   * Check user PIN
   * @param {Object} params - PIN check parameters
   * @returns {Promise} Response from API
   */
  checkPin(params = {}) {
    return this.#handleRequest(checkPin(params))
  }

  /**
   * Add user PIN
   * @param {Object} params - PIN creation parameters
   * @returns {Promise} Response from API
   */
  addPin(params = {}) {
    return this.#handleRequest(addPin(params))
  }

  /**
   * Update user PIN
   * @param {Object} params - PIN update parameters
   * @returns {Promise} Response from API
   */
  updatePin(params = {}) {
    return this.#handleRequest(updatePin(params))
  }

  /**
   * Get balance history
   * @param {Object} params - Query parameters for balance history
   * @returns {Promise} Response from API
   */
  getBalanceHistory(params = {}) {
    return this.#handleRequest(getBalanceHistory(params))
  }

  /**
   * Get balance history detail
   * @param {string|number} balanceHistoryId - ID of the balance history entry
   * @returns {Promise} Response from API
   */
  getBalanceHistoryDetail(balanceHistoryId) {
    return this.#handleRequest(getBalanceHistoryDetail(balanceHistoryId))
  }

  /**
   * Get list of banks
   * @returns {Promise} Response from API
   */
  getBankList() {
    return this.#handleRequest(getBankList())
  }

  /**
   * Get user's bank accounts
   * @returns {Promise} Response from API
   */
  getBankAccounts() {
    return this.#handleRequest(getBankAccounts())
  }

  /**
   * Check bank account
   * @param {Object} params - Bank account check parameters
   * @returns {Promise} Response from API
   */
  checkBankAccount(params = {}) {
    return this.#handleRequest(checkBankAccount(params))
  }

  /**
   * Add bank account
   * @param {Object} params - Bank account creation parameters
   * @returns {Promise} Response from API
   */
  addBankAccount(params = {}) {
    return this.#handleRequest(addBankAccount(params))
  }

  /**
   * Update bank account
   * @param {string|number} bankAccountId - ID of bank account to update
   * @param {Object} params - Bank account update parameters
   * @returns {Promise} Response from API
   */
  updateBankAccount(bankAccountId, params = {}) {
    return this.#handleRequest(updateBankAccount(bankAccountId, params))
  }

  /**
   * Delete bank account
   * @param {string|number} bankAccountId - ID of bank account to delete
   * @returns {Promise} Response from API
   */
  deleteBankAccount(bankAccountId) {
    return this.#handleRequest(deleteBankAccount(bankAccountId))
  }

  /**
   * Get all addresses
   * @returns {Promise} Response from API
   */
  getAddresses(type) {
    return this.#handleRequest(getAddresses(type))
  }

  /**
   * Get address detail
   * @param {string|number} addressId - ID of the address
   * @returns {Promise} Response from API
   */
  getAddressDetail(addressId) {
    return this.#handleRequest(getAddressDetail(addressId))
  }

  /**
   * Add new address
   * @param {Object} params - Address creation parameters
   * @returns {Promise} Response from API
   */
  addAddress(params = {}) {
    return this.#handleRequest(addAddress(params))
  }

  /**
   * Update address
   * @param {string|number} addressId - ID of address to update
   * @param {Object} params - Address update parameters
   * @returns {Promise} Response from API
   */
  updateAddress(addressId, params = {}) {
    return this.#handleRequest(updateAddress(addressId, params))
  }

  /**
   * Delete address
   * @param {string|number} addressId - ID of address to delete
   * @returns {Promise} Response from API
   */
  deleteAddress(addressId) {
    return this.#handleRequest(deleteAddress(addressId))
  }

  /**
   * Set address as main
   * @param {string|number} addressId - ID of address to set as main
   * @returns {Promise} Response from API
   */
  setMainAddress(addressId) {
    return this.#handleRequest(setMainAddress(addressId))
  }

  /**
   * Get app version
   * @returns {Promise} Response from API
   */
  getAppVersion() {
    return this.#handleRequest(getVersion())
  }
}

const Account = new HttpAccountClient();
Object.freeze(Account);

export default Account;
